import React, { useEffect, useMemo, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// NOTE:
// This component uses framer-motion for clean SVG morph + stagger.
// Install: npm i framer-motion
import { motion } from "framer-motion";

const visualVariants = cva(
  "relative inline-flex items-center justify-center select-none",
  {
    variants: {
      size: {
        sm: "w-[140px] h-[44px]",
        md: "w-[180px] h-[56px]",
        lg: "w-[240px] h-[72px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

export type HealthcareDataVisualProps = VariantProps<typeof visualVariants> & {
  className?: string;
};

/**
 * Hover-activated SVG visual:
 * - Default: animated ECG line (rose)
 * - Hover: morph into bar chart + stagger growth (cyan)
 */
export default function HealthcareDataVisual({
  size,
  className,
}: HealthcareDataVisualProps) {
  const [hovered, setHovered] = useState(false);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(pointer: coarse)");

    const update = () => setIsCoarsePointer(Boolean(mql.matches));
    update();

    // Prefer modern API; older Safari won't fire updates (acceptable fallback).
    if ("addEventListener" in mql) {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
  }, []);

  // Paths are built as polylines (many L segments) to make interpolation stable.
  const { ecgPath, barsPath } = useMemo(() => {
    // ECG baseline around y=30
    const ecg = [
      [0, 30],
      [16, 30],
      [22, 26],
      [28, 34],
      [34, 30],
      [46, 30],
      [56, 30],
      [62, 18],
      [68, 44],
      [74, 24],
      [80, 30],
      [92, 30],
      [102, 28],
      [112, 32],
      [124, 30],
      [144, 30],
      [160, 30],
      [176, 30],
      [200, 30],
    ] as const;

    // Bars: draw baseline, then rectangles via step-like polyline.
    // Keep same number of points as ECG for stable morphing.
    const bars = [
      [0, 46],
      [20, 46],
      [20, 18],
      [32, 18],
      [32, 46],
      [50, 46],
      [50, 26],
      [64, 26],
      [64, 46],
      [84, 46],
      [84, 14],
      [98, 14],
      [98, 46],
      [120, 46],
      [120, 30],
      [134, 30],
      [134, 46],
      [160, 46],
      [180, 46],
      [200, 46],
    ] as const;

    const toPath = (pts: readonly (readonly [number, number])[]) =>
      pts.map((p, idx) => `${idx === 0 ? "M" : "L"}${p[0]} ${p[1]}`).join(" ");

    return {
      ecgPath: toPath(ecg),
      barsPath: toPath(bars),
    };
  }, []);

  return (
    <div
      className={cn(visualVariants({ size }), className)}
      onMouseEnter={() => {
        if (!isCoarsePointer) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!isCoarsePointer) setHovered(false);
      }}
      onPointerDown={(event) => {
        // Touch devices: allow a tap-to-toggle micro-interaction.
        if (!isCoarsePointer) return;
        event.preventDefault();
        setHovered((v) => !v);
      }}
      role={isCoarsePointer ? "button" : "img"}
      tabIndex={isCoarsePointer ? 0 : undefined}
      aria-pressed={isCoarsePointer ? hovered : undefined}
      aria-label={
        isCoarsePointer
          ? "Toggle healthcare to analytics visual"
          : "Healthcare to analytics visual"
      }
    >
      <svg
        viewBox="0 0 200 60"
        className="absolute inset-0 h-full w-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="health-data-gradient" x1="0" y1="0" x2="200" y2="0">
            <stop offset="0%" stopColor="#FF3366" />
            <stop offset="60%" stopColor={hovered ? "#00F2FE" : "#FF3366"} />
            <stop offset="100%" stopColor="#00F2FE" />
          </linearGradient>
          <filter id="soft-glow" x="-10%" y="-50%" width="120%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ECG / Bars morph line */}
        <motion.path
          d={ecgPath}
          animate={{ d: hovered ? barsPath : ecgPath }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          stroke="url(#health-data-gradient)"
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
          filter="url(#soft-glow)"
        />

        {/* Draw-on effect when NOT hovered */}
        {!hovered && (
          <motion.path
            d={ecgPath}
            stroke="#FF3366"
            strokeOpacity={0.55}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ pathLength: 1 }}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1] }}
            transition={{ duration: 1.2, ease: "linear", repeat: Infinity }}
          />
        )}

        {/* Bar bounce (subtle) on hover */}
        {hovered && (
          <g>
            {[28, 56, 91, 127, 156].map((x, idx) => (
              <motion.rect
                // Keys stable
                key={x}
                x={x}
                y={12}
                width={10}
                height={36}
                fill="#00F2FE"
                opacity={0.12}
                initial={{ scaleY: 0.75 }}
                animate={{ scaleY: [0.75, 1, 0.92, 1] }}
                transition={{
                  duration: 0.8,
                  delay: idx * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{ transformOrigin: "center bottom" }}
              />
            ))}
          </g>
        )}
      </svg>

      <div className="absolute inset-0 border border-border" aria-hidden="true" />
    </div>
  );
}
